// InstallBanner — instalación PWA (Android prompt nativo / guía iOS) +
// card "Activar alertas en el teléfono" (push FCM, gateada por acceso).
// Descartable: no vuelve a aparecer por 14 días (localStorage).
// No se muestra en modo standalone (ya instalada).
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { SERVICE_PAUSED } from '../config/servicePaused';
import usePushPermission from '../hooks/usePushPermission';

const LS_INSTALL = 'ee-install-dismissed';
const DISMISS_DAYS = 14;

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const recentlyDismissed = () => {
  try {
    const ts = Number(localStorage.getItem(LS_INSTALL) || 0);
    return Date.now() - ts < DISMISS_DAYS * 864e5;
  } catch (_e) { return false; }
};

const S = {
  wrap: {
    position: 'fixed', left: 12, right: 12, bottom: 12, zIndex: 9999,
    background: '#17171a', color: '#f5f5f5', border: '1px solid #2c2c31',
    borderRadius: 14, padding: '14px 16px', boxShadow: '0 8px 28px rgba(0,0,0,.45)',
    display: 'flex', alignItems: 'center', gap: 12, maxWidth: 480, margin: '0 auto',
  },
  icon: { width: 40, height: 40, borderRadius: 9, flexShrink: 0 },
  title: { fontWeight: 700, fontSize: 14, marginBottom: 2 },
  body: { fontSize: 12.5, opacity: 0.85, lineHeight: 1.35 },
  btn: {
    background: '#FF6B2C', color: '#fff', border: 'none', borderRadius: 9,
    padding: '9px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer', flexShrink: 0,
  },
  close: {
    background: 'transparent', color: '#9a9aa2', border: 'none', fontSize: 18,
    cursor: 'pointer', padding: 4, lineHeight: 1, flexShrink: 0,
  },
};

export default function InstallBanner() {
  const { user, hasActiveAccess } = useAuth();
  const { pathname } = useLocation();
  const push = usePushPermission();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  // Solo en rutas del dashboard, logueado, no instalada, no descartada.
  const inDash = pathname.startsWith('/dashboard') || pathname === '/historial' ||
    pathname === '/performance' || pathname === '/config';
  // Con el servicio en pausa no tiene sentido invitar a instalar la app:
  // lo unico que se ve adentro es el aviso de pausa.
  if (SERVICE_PAUSED) return null;
  if (!inDash || !user || hidden || isStandalone()) return null;

  const dismissInstall = () => {
    try { localStorage.setItem(LS_INSTALL, String(Date.now())); } catch (_e) { /* noop */ }
    setHidden(true);
  };

  // ── Card PUSH (prioridad: si ya está instalada en iOS, o Android con
  //    permiso pendiente y acceso vigente) ─────────────────────────────
  if (push.canRequest) {
    return (
      <div style={S.wrap}>
        <img src="/icons/icon-192.png" alt="" style={S.icon} />
        <div style={{ flex: 1 }}>
          <div style={S.title}>Activar alertas en el teléfono</div>
          <div style={S.body}>Recibe las señales EG1 al instante, incluso con la app cerrada.</div>
        </div>
        <button style={S.btn} onClick={push.request}>
          {push.status === 'prompt' ? '...' : 'Activar'}
        </button>
        <button style={S.close} onClick={push.dismiss} aria-label="Cerrar">×</button>
      </div>
    );
  }

  if (recentlyDismissed()) return null;

  // ── Banner instalación Android (prompt nativo capturado) ────────────
  if (deferredPrompt) {
    const install = async () => {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      dismissInstall();
    };
    return (
      <div style={S.wrap}>
        <img src="/icons/icon-192.png" alt="" style={S.icon} />
        <div style={{ flex: 1 }}>
          <div style={S.title}>Instala Edge Elite</div>
          <div style={S.body}>Acceso directo + alertas push en tu teléfono.</div>
        </div>
        <button style={S.btn} onClick={install}>Instalar app</button>
        <button style={S.close} onClick={dismissInstall} aria-label="Cerrar">×</button>
      </div>
    );
  }

  // ── Guía iOS (Safari, sin prompt nativo) ────────────────────────────
  if (isIOS() && (hasActiveAccess || push.needsInstallFirst)) {
    return (
      <div style={S.wrap}>
        <img src="/icons/icon-192.png" alt="" style={S.icon} />
        <div style={{ flex: 1 }}>
          <div style={S.title}>Instala Edge Elite en tu iPhone</div>
          <div style={S.body}>
            1. Toca <strong>Compartir</strong> (□↑) abajo en Safari<br />
            2. Elige <strong>“Agregar a pantalla de inicio”</strong>
          </div>
        </div>
        <button style={S.close} onClick={dismissInstall} aria-label="Cerrar">×</button>
      </div>
    );
  }

  return null;
}
